import { useCallback, useMemo, useState } from 'react';
import { Card, Col, Flex, Row, Typography } from 'antd';
import {
  IconBuildingEstate,
  IconCoinEuro,
  IconPercentage,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import type { LandlordPartnerRowDto } from '@evimo/client/models';
import { AuthGuard } from '~/app/auth/guard/AuthGuard';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { PartnersService } from '~/app/services/partners/partners.service';
import { SlaKpiCard } from '~/app/landing/sla/shared/components/SlaKpiCard';
import { SlaPageHeader } from '~/app/landing/sla/shared/components/SlaPageHeader';
import {
  SLA_SECTION_CARD_CLASS,
  SLA_TABLE_SCROLL_X_LANDLORD,
  SLA_TABLE_SCROLL_Y,
} from '~/app/landing/sla/shared/sla.constants';
import { formatEuro } from '~/app/landing/sla/shared/utils/sla-format.utils';
import { useSlaPartnerTableExpandable } from '~/app/landing/sla/shared/hooks/useSlaPartnerTableExpandable';
import { LandlordPartnerExpandedRow } from '@/app/landing/sla/landlords/components/LandlordPartnerExpandedRow';
import { LandlordPartnerModal } from '@/app/landing/sla/landlords/LandlordPartnerModal/LandlordPartnerModal';
import { useLandlordColumns } from '@/app/landing/sla/landlords/hooks/useLandlordColumns';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

export function LandlordsPageContainer() {
  const { t } = useTranslation('sla');
  const { hasPermission } = usePermission();
  const { user } = useAuth();
  const canManage = hasPermission(PermissionKey.PARTNERS_LANDLORDS_MANAGE);
  const organizationId = user?.organizationId ?? '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<LandlordPartnerRowDto | null>(
    null,
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['partners', 'landlords', organizationId],
    queryFn: () => PartnersService.listLandlords(organizationId),
    enabled: !!organizationId,
  });

  const openCreate = () => {
    setEditingRow(null);
    setModalOpen(true);
  };

  const columns = useLandlordColumns({
    canManage,
    onEdit: (row) => {
      setEditingRow(row);
      setModalOpen(true);
    },
  });

  const renderExpandedRow = useCallback(
    (row: LandlordPartnerRowDto) => <LandlordPartnerExpandedRow row={row} />,
    [],
  );

  const expandable = useSlaPartnerTableExpandable({
    renderExpandedRow,
    rowExpandable: (row) => !!row.slaContract,
  });

  const tableLocale = useMemo(
    () => ({
      emptyText: (
        <Flex vertical align="center" gap={8} style={{ padding: '32px 0' }}>
          <Typography.Text type="secondary">
            {t('landlords.empty')}
          </Typography.Text>
        </Flex>
      ),
    }),
    [t],
  );

  return (
    <AuthGuard requiredPermissions={[PermissionKey.PARTNERS_LANDLORDS_READ]}>
      <div className="app-page app-page--sla">
        <SlaPageHeader
          title={t('landlords.title')}
          subtitle={t('landlords.subtitle')}
          addLabel={t('landlords.add')}
          onAdd={openCreate}
          canAdd={canManage}
          icon={<IconBuildingEstate size={26} stroke={1.6} />}
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} xl={8}>
            <SlaKpiCard
              title={t('landlords.kpi.activeSlas')}
              value={data?.activeSlaCount ?? 0}
              icon={<IconShieldCheck size={24} stroke={1.6} />}
              accentColor="#1e6df6"
            />
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <SlaKpiCard
              title={t('landlords.kpi.avgUptime')}
              value={`${(data?.avgUptimeTargetPct ?? 0).toFixed(1)}%`}
              icon={<IconPercentage size={24} stroke={1.6} />}
              accentColor="#00a87e"
            />
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <SlaKpiCard
              title={t('landlords.kpi.avgPenalty')}
              value={formatEuro(data?.avgPenaltyPerDay ?? 0)}
              icon={<IconCoinEuro size={24} stroke={1.6} />}
              accentColor="#e23b4a"
            />
          </Col>
        </Row>

        <Card
          className={SLA_SECTION_CARD_CLASS}
          title={
            <span className="ds-section-header">
              {t('landlords.tableTitle')}
            </span>
          }
          styles={{ body: { padding: 0 } }}
        >
          <EvimoTable
            className="sla-partner-table"
            rowKey="relationshipId"
            loading={isLoading}
            columns={columns}
            dataSource={data?.items ?? []}
            pagination={false}
            scroll={{ x: SLA_TABLE_SCROLL_X_LANDLORD, y: SLA_TABLE_SCROLL_Y }}
            locale={tableLocale}
            size="middle"
            expandable={expandable}
          />
        </Card>

        <LandlordPartnerModal
          open={modalOpen}
          organizationId={organizationId}
          editingRow={editingRow}
          onClose={() => {
            setModalOpen(false);
            setEditingRow(null);
          }}
          onSuccess={() => {
            setModalOpen(false);
            setEditingRow(null);
            void refetch();
          }}
        />
      </div>
    </AuthGuard>
  );
}
